import { ReactNode } from 'react'
import { IconCircleBanSign, IconXMark } from '@sequencehq/core-components'
import { NodeTimeline } from 'Automations/components'
import { ExecutionSummaryComponentProps } from './types'

type Tasks = ExecutionSummaryComponentProps['tasks']
type ReviewableTasks = ExecutionSummaryComponentProps['reviewableTasks']

const IN_PROGRESS_STATUSES = new Set(['IN_PROGRESS', 'SCHEDULED'])

export const isTaskInProgress = (task: Tasks[number]): boolean =>
  IN_PROGRESS_STATUSES.has(task.status)

export const hasAllTasksInProgress = (tasks: Tasks): boolean =>
  tasks.every(isTaskInProgress)

export const hasFailedTask = (tasks: Tasks): boolean =>
  tasks.some(
    task =>
      task.status === 'FAILED' ||
      task.status === 'FAILED_WITH_TERMINAL_ERROR' ||
      task.status === 'TIMED_OUT'
  )

export const hasCancelledTask = (tasks: Tasks): boolean =>
  tasks.some(task => task.status === 'CANCELED')

export const findReviewableTask = (
  tasks: Tasks,
  reviewableTasks: ReviewableTasks
) => {
  const taskIds = new Set(tasks.map(t => t.taskId))
  return reviewableTasks?.find(
    rt => rt.status === 'PENDING' && taskIds.has(rt.executorTaskId)
  )
}

/**
 * Renders a terminal-state timeline item (failed/cancelled/custom icon)
 * used across multiple execution summary components.
 */
export const TerminalStateItem = ({
  icon,
  label,
  timestamp,
  isLatestNodeExecution,
  'data-testid': dataTestId
}: {
  icon?: ReactNode
  label: string
  timestamp?: string
  isLatestNodeExecution?: boolean
  'data-testid'?: string
}) => (
  <NodeTimeline.Item
    data-testid={dataTestId}
    icon={icon ?? <IconXMark />}
    showStem={!isLatestNodeExecution}
  >
    <NodeTimeline.Title>
      <NodeTimeline.UserAction label={label} timestamp={timestamp} />
    </NodeTimeline.Title>
  </NodeTimeline.Item>
)

export const FailedItem = (props: {
  label: string
  timestamp?: string
  isLatestNodeExecution?: boolean
  'data-testid'?: string
}) => <TerminalStateItem icon={<IconXMark />} {...props} />

export const CancelledItem = (props: {
  label: string
  timestamp?: string
  isLatestNodeExecution?: boolean
  'data-testid'?: string
}) => <TerminalStateItem icon={<IconCircleBanSign />} {...props} />

/**
 * Maps an array of user IDs to their email addresses
 * using a pre-fetched list of users.
 */
export const resolveUserEmails = (
  userIds: Array<string> | undefined,
  users: Array<{ id: string; email: string }>
): Array<string> =>
  (userIds ?? [])
    .map(id => users.find(user => user.id === id)?.email)
    .filter((email): email is string => !!email)
